import * as React from 'react';
import Link from '@dashboard/components/Link';
import { TopBarSearchButton } from '@dashboard/layouts/NavigationParts';
import { SidebarTrigger, useOptionalSidebar } from '@dashboard/components/ui/sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@dashboard/components/ui/breadcrumb';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  topBarActions?: React.ReactNode;
  children?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Off where the list sits inside a detail page that already has the top bar. */
  topBar?: boolean;
}

// Search and the top bar's actions line up with the content column's edges.
const CONTENT_INSET = 'max(0px, calc((100% - 80rem) / 2))';
const SEARCH_START = { marginLeft: CONTENT_INSET };
const ACTIONS_END = { marginRight: CONTENT_INSET };

export function PageHeader({
  title,
  description,
  actions,
  topBarActions,
  children,
  breadcrumbs,
  topBar = true,
}: PageHeaderProps) {
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const sidebar = useOptionalSidebar();

  return (
    <>
      {topBar && (
        <div
          data-page-top-bar
          className="relative -mx-4 -mt-4 mb-4 hidden h-14 items-center gap-2 border-b border-gray-200 dark:border-border px-4 md:flex"
        >
          {sidebar && (
            <div className="absolute inset-y-0 left-3 flex items-center gap-2 lg:hidden">
              <SidebarTrigger
                className="size-8 text-muted-foreground"
                aria-label={sidebar.state === 'collapsed' ? 'Open sidebar' : 'Close sidebar'}
              />
            </div>
          )}
          <div className="w-full max-w-xs max-lg:ml-9!" style={SEARCH_START}>
            <TopBarSearchButton />
          </div>
          {topBarActions && (
            <div className="ml-auto flex shrink-0 items-center gap-2" style={ACTIONS_END}>
              {topBarActions}
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {hasBreadcrumbs && (
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, i) => (
                <React.Fragment key={crumb.label}>
                  {i > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem>
                    {crumb.href ? (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        )}
        <div>
          <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <h1 className="truncate text-lg font-semibold text-gray-900 dark:text-foreground">
              {title}
            </h1>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
            {/* The top bar is desktop only, so its actions sit by the title on mobile. */}
            {topBarActions && (
              <div className="flex items-center gap-2 md:hidden">{topBarActions}</div>
            )}
          </div>
          {description && (
            <p className="text-sm text-gray-500 dark:text-muted-foreground">{description}</p>
          )}
        </div>
        {children}
      </div>
    </>
  );
}

export default PageHeader;
